/** constants, which are used in several components and/or pages */

import {
  IApplicationSubmissionData,
  IAttachmentDefinition,
  IChallenge,
  IFund,
  IFundApplication,
  IProject,
  IProposal,
  IProposalContent,
  IProposalSubmissionData,
  ObligationState,
  ProjectState,
  ProposalState,
  ProposalType,
  SelfAssessment
} from "@api/schema"
import { AvailableContentItems } from "@components/common/contentmanagement/ContentManagementHelper"

import { BlockerContext, CommonBlockers } from "./blocker"
import {
  availableForSubmission,
  challengeStatesAfterAllowingToSubmitProposals,
  challengeTypeAllowedForSelection,
  hasConcretizations,
  hasNotReachedSubmissionBeginYet,
  isSubmissionPeriodStillValid
} from "./challengeHelper"
import { isFund } from "./fundHelper"
import { getResourceRequirementsFundedByFund, sumRequirementsCosts } from "./projectHelper"
import { idFromIModelOrIRI, iriFromIModelOrString, isEmptyNullOrUndefinedObject } from "./util"



/**
 * proposalHelper: Collection of helper-functions for the proposal system
 *
 */

/* ************************************************************************** */
/* constants                                                                  */
/* ************************************************************************** */

export const hashIdProposalId = "proposal"

/**
 * list of blockers for submitting a proposal
 */
export enum ProposalSubmissionBlocker {
  ProposalDoesNotMatchProject = "common:proposal.blocker.notMatchingProject",
  ProposalDoesNotMatchChallenge = "common:proposal.blocker.notMatchingChallenge",
  ProposalAlreadySubmitted = "common:proposal.blocker.alreadySubmitted",
  ProjectDeactivated = "common:project.blocker.deactivated",
  ProjectLocked = "common:project.blocker.locked",
  ProposalNotActive = "common:proposal.blocker.notActive",
  ProposalSelfAssessmentNotFulfilled = "common:proposal.blocker.selfAssessmentNotFulfilled",
  ProjectProfileSelfAssessmentNotFulfilled = "common:project.blocker.profileSelfAssessmentNotFulfilled",
  ProjectPlanSelfAssessmentNotFulfilled = "common:project.blocker.planSelfAssessmentNotFulfilled",
  ConcretizationSelfAssessmentNotFulfilled = "common:proposal.blocker.concretizationSelfAssessmentNotFulfilled",
  AttachmentsMissing = "common:proposal.blocker.attachmentsMissing",
  RequestedFundingTooLow = "common:proposal.blocker.requestedFundingTooLow",
  RequestedFundingTooHigh = "common:proposal.blocker.requestedFundingTooHigh",
}

/* ************************************************************************** */
/* Pre-filled data for initial objects based on existing schmemas             */
/* using the prefix "Fresh" to have a standard in the code                    */
/* ************************************************************************** */

/**
 * FreshProposalContent delivers a pre-filled IProposalContent to be used as an standard fresh proposalContent.
 */
export const FreshProposalContent = (contentId: AvailableContentItems): IProposalContent => {
  return {
    contentId,
    requirement: ObligationState.Required,
    version: 1
  }
}

/* ************************************************************************** */
/* helper-functions                                                           */
/* ************************************************************************** */

/**
 * Check, if a not submitted proposal can not be submitted anymore for this challenge
 *
 * @todo: Umbau in eine Blocker-Funktion
 * @todo: Check for FundApplicationState!
 */
export const challengeExpiredForProposal = (proposal: IProposal | IFundApplication, challenge: IChallenge): boolean => {
  return isEmptyNullOrUndefinedObject(proposal)
    && isEmptyNullOrUndefinedObject(challenge)
    && proposal.state === ProposalState.Preparing
    && (challengeStatesAfterAllowingToSubmitProposals(challenge)
      || (challenge.directSubmission
        && (!isSubmissionPeriodStillValid(challenge)
          && !hasNotReachedSubmissionBeginYet(challenge))))
}

/**
 * Checking, if given property is a proposal or a fundapplication based on the type.
 */
export const isFundApplication = (proposal: IProposal | IFundApplication): boolean =>
  proposal?.["@type"] === ProposalType.Fundapplication

/**
 * Building the hash string for a proposal in order to add it in the url.
 *
 * if the proposal has the id 2, the output would: #proposalID-2
 *
 * E.g. the new url would look like: "/projects/[slug]/challenge-proposal#proposalID-2
 *
 * @param proposal
 * @returns e.g. #proposalID-2, if proposal is defined, otherwise an empty String
 */
export const buildHashStringForProposal = (proposal: IProposal | IFundApplication): string => proposal ? "#" + hashIdProposalId + "-" + idFromIModelOrIRI(proposal).toString() : ""

/**
 * Determines, if the given submissionData has the property requestedFunding.
 * If it is true, than the submissionData is a type of IApplicationSubmissionData.
 *
 * @param submissionData
 * @returns true, if the submissionData has the property requestedFunding
 */
export const hasPropertyRequestFunding = (submissionData: IProposalSubmissionData | IApplicationSubmissionData): boolean => {
  return Object.hasOwn(submissionData, 'requestedFunding')
}


/** are all required Attachments attached?
 *
 * @param proposal the proposal to check
 * @param challenge the corresponding challenge
 * @returns true, if at least one Attachment is required by the challenge and not provided by the proposal
 */
export const requiredAttachmentsAreAttached = (challenge: IChallenge, proposal: IProposal | IFundApplication): boolean => {
  return missingRequiredAttachments(proposal, challenge).length === 0
}

/**
 * @param proposal the proposal to check
 * @param challenge the corresponding challenge
 * @returns a list of IAttachmentDefinitions, that are required by the challenge and are not provided by the proposal
 */
export const missingRequiredAttachments = (proposal: IProposal | IFundApplication, challenge: IChallenge): IAttachmentDefinition[] => {
  const missingAttachments: IAttachmentDefinition[] = []
  // if no attachments is required, return an empty list
  if (!atLeastOneAttachmentIsRequired(challenge)) {
    return missingAttachments
  }
  // if at least one attachment is required check for corresponding missing attachments
  challenge.attachmentDefinitions
    // filter attachmentDefinitions that are required
    .filter(aDef => aDef.required)
    // for all existing attachmentDefinitions to that challenge...
    .forEach(aDef => {
      // ... find the corresponding attachment
      if (!proposal.attachments?.find(att => idFromIModelOrIRI(att.definition) === idFromIModelOrIRI(aDef))) {
        missingAttachments.push(aDef)
      }
    })

  return missingAttachments
}

/**
 * Check, if at least one AttachmentDefinition is marked as "required"
 *
 * @param challenge challenge to be checked for AttachmentDefinitions
 * @returns if at least one attachment is required, return true
 */
export const atLeastOneAttachmentIsRequired = (challenge: IChallenge): boolean => !!challenge.attachmentDefinitions?.find(a => a.required === true)


// #region usecaseKey creator functions

/**
 * Helper to create a standardized unique string
 * to be used as usecaseKey for API-calls
 * for approved proposals of a challenge.
 *
 * @challengeId id of the challenge
 * @returns a unique string to be used as usecaseKey
 */
export const usecaseKeyForApprovedProposalCollectionByChallenge = (challengeId: number): string =>
  `proposals_approved_for_challenge_${challengeId.toString()}`

/**
 * Helper to create a standardized unique string
 * to be used as usecaseKey for API-calls
 * for proposals with a given proposal state of a challenge.
 *
 * @param challengeId id of the challenge
 * @param state
 * @returns a unique string to be used as usecaseKey
 */
export const usecaseKeyForProposalCollectionByProposalStateAndChallenge = (challengeId: number, state: ProposalState): string =>
  `proposals_state_${state}_for_challenge_${challengeId.toString()}`

/**
 * Helper to create a standardized unique string
 * to be used as usecaseKey for API-calls
 * for proposals by a challenge.
 *
 * @param challengeId id of the challenge
 * @param state
 * @returns a unique string to be used as usecaseKey
 */
export const usecaseKeyForProposalCollectionByChallenge = (challengeId: number): string =>
  `proposals_for_challenge_${challengeId.toString()}`
// #endregion

/** ****************************************************************************************
 * The following section is about helper methods for handling the proposal workflow
 *
 * Follow links should be provided on certain pages, if the user has the rights and the conditions for the links are fullfilled:
 * 'which page' => left link, center link, right link:
 *
 * challenge-selection => project-plan, proposal-dashboard|project-dashboard,none
 * proposal-dasboard/index => project-plan, proposal-dashboard, none
 * StandingDataView => none, proposal-dasboard, conretizations|attachments|submission|post-submission|in-active-proposal
 * StandingDataEdit => none
 * concretizations => StandingDataView, proposal-dasboard, attachments|submission|post-submission|in-active-proposal
 * attachments => StandingDataView|concretizations, proposal-dasboard, submission|post-submission|in-active-proposal
 * submission => StandingDataView|conretizations|attachments, proposal-dasboard, none
 * post-submission => StandingDataView|conretizations|attachments, proposal-dasboard, none
 * in-active-proposal => StandingDataView|conretizations|attachments, proposal-dasboard, none
 * module-inactive-proposal-details => none, proposal-dashboard, none
 */

/**
 * checks if the necessary team contact is defined in the project
 */
export const standingDataIsComplete = (project: IProject): boolean => {
  return project.teamContact !== null && project.teamContact !== undefined
}


/**
 * Test, if following requirements are fullfilled.
 * This is used to check, if a proposal page fullfill these condition in order to be accessed.
 * Some proposal page requiere additional checks in order to be accessed
 */
export const checkForGeneralAccessProposalPageRequirements = (project: IProject): boolean => {
  return !isEmptyNullOrUndefinedObject(project)
    && !project.locked
    && (project.state === ProjectState.Active || project.state === ProjectState.Inactive)
  // && user.active && user.validated => @todo? will be check in the pages through the component withAut
}

/**
 * checks if the given challenge has concretizations and the given proposal answers
 * all concretization questions with at least one character
 *
 * Hint: The challenge has to be loaded!
 *
 * @todo: currently unused (21.09.2023)
 *
 * @param proposal an proposal to that challenge
 * @param challenge optional: a explicitely given challenge, otherwise p.challenge is used
 * @returns true, if all
 */
export const concretizationsComplete = (proposal: IProposal | IFundApplication, challenge?: IChallenge): boolean => {
  const usedChallenge = challenge ? challenge : proposal.challenge
  // return true, if the challenge does not use concretizations
  if (typeof usedChallenge !== "string") {
    if (!hasConcretizations(usedChallenge)) {
      return true
    } else {
      // otherwise check, if all needed concretizations are filled
      let concComplete = true
      usedChallenge.concretizations.forEach(ch => {
        if (!proposal.concretizations[ch.id] || proposal.concretizations[ch.id].length === 0) {
          concComplete = false
        }
      })

      /**
       * may be better as:
       */
      // const concComplete = usedChallenge.concretizations
      //   .map(ch => !proposal.concretizations[ch.id] || proposal.concretizations[ch.id].length === 0)
      //   .reduce((accumulator, currentValue) => accumulator && currentValue, true)

      return concComplete
    }
  }
  return false
}

/**
 * @return the state of the proposal transformed into a valid translation key,
 * or if a challenge is given: "unfinished", if the proposal was not submitted and
 * it is too late to submit it
 */
export const proposalStateAsTranslationKey = (proposal: IProposal | IFundApplication, challenge?: IChallenge): string => {
  if (challenge && proposal?.state === ProposalState.Preparing && !availableForSubmission(challenge)) {
    return "proposal.state.unfinished"
  }

  if (proposal.state === ProposalState.Finished) {
    if (proposal.approved) {
      return "proposal.state.finished.approved"
    }

    return "proposal.state.finished.notApproved"
  }

  // @todo: check: FundApplicationState berücksichtigt?
  return "proposal.state." + proposal.state
}



/**
 * Returns an ordered list of blockers, that explains why the given proposal may not be submitted to the given challenge.
 * Use entry number 0 to get the most urgent/most global reason; or show all entries to give the user
 * a complete overview. If the returned array has no entries, the proposal may be submitted:
 * no blocker found!
 *
 * NOTE: use in combination with challengeHelper.blockerToAcceptSubmissions() to check the blockers
 * of the challenge itself. Those blockers are more urgent/global than the proposal blockers!
 *
 * This function should be the single point of truth for "proposal is ready for submission" and all other functions
 * should use this function!
 *
 * NOTE: the resulting translation keys may need params, that should be given as parameters
 * when translating the keys. Known params: (25.07.2023)
 * submissionBegin
 * submissionEnd
 *
 * @param proposal the proposal to be checked
 * @param challenge the corresponding challenge
 * @returns a list of translation keys of the blockers, why a given proposal may not be submitted to the given challenge
 *
 * @todo: komplettieren: thirdPartyFundsExplanation, Kostenarten,
 */
export const blockerToSubmitProposal = (project: IProject, proposal: IProposal | IFundApplication, challenge: IChallenge): BlockerContext => {
  const blockers: BlockerContext = {
    blocker: []
  }

  // NOTE: the order of this messages is IMPORTANT!
  // more global/urgent blockers must be checked first, less global/urgent blockers must be checked/attached later!


  // #region coder checks: should never happen if code is build correctly
  // @todo logging

  // technical checks
  // @todo entity abhängige fehlermeldungen?
  if (isEmptyNullOrUndefinedObject(proposal)) {
    return { blocker: [CommonBlockers.ObjectNotFound] }
  }
  if (isEmptyNullOrUndefinedObject(challenge)) {
    return { blocker: [CommonBlockers.ObjectNotFound] }
  }
  if (isEmptyNullOrUndefinedObject(project)) {
    return { blocker: [CommonBlockers.ObjectNotFound] }
  }

  // checks for valid connections between given entities
  if (iriFromIModelOrString(proposal.challenge) !== challenge["@id"]) {
    return { blocker: [ProposalSubmissionBlocker.ProposalDoesNotMatchChallenge] }
  }
  if (!project.proposals?.find(p => p.id === proposal.id)) {
    return { blocker: [ProposalSubmissionBlocker.ProposalDoesNotMatchProject] }
  }
  // #endregion

  // submission check
  if (proposal.state !== ProposalState.Preparing) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProposalAlreadySubmitted)
  }

  // #region project checks
  if (project.locked) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProjectLocked)
  }
  if (project.state === ProjectState.Deactivated) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProjectDeactivated)
  }
  if (project.profileSelfAssessment !== SelfAssessment.Complete) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProjectProfileSelfAssessmentNotFulfilled)
  }
  if (project.planSelfAssessment !== SelfAssessment.Complete) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProjectPlanSelfAssessmentNotFulfilled)
  }
  // #endregion


  // #region proposal checks
  // @todo test for Stammdaten completed? -> standingDataIsComplete() -> depends, if standing data is wanted (Cottbus only wants some of this data, not all of it!)
  // @todo test for ProposalContents
  if (!proposal.active) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProposalNotActive)
  }
  if (proposal.proposalSelfAssessment !== SelfAssessment.Complete) {
    blockers.blocker.push(ProposalSubmissionBlocker.ProposalSelfAssessmentNotFulfilled)
  }

  // check for concretizations & attachments
  if (hasConcretizations(challenge) && proposal.concretizationSelfAssessment !== SelfAssessment.Complete) {
    blockers.blocker.push(ProposalSubmissionBlocker.ConcretizationSelfAssessmentNotFulfilled)
  }

  // @todo: create blocker collection for every single missing attachment to be more precise for the user feedback
  if (missingRequiredAttachments(proposal, challenge).length > 0) {
    blockers.blocker.push(ProposalSubmissionBlocker.AttachmentsMissing)
  }
  // #endregion

  // #region fund specific checks/conditions
  if (isFund(challenge)) {
    const requestedFunding = sumRequirementsCosts(getResourceRequirementsFundedByFund(project, challenge))
    const fund = challenge as IFund

    if (requestedFunding < fund.minimumGrant) {
      blockers.blocker.push(ProposalSubmissionBlocker.RequestedFundingTooLow)
    }
    if (requestedFunding > fund.maximumGrant) {
      blockers.blocker.push(ProposalSubmissionBlocker.RequestedFundingTooHigh)
    }
  }
  // #endregion

  return blockers
}

// #region workflow check functions
/** ****************************************************** */
/* page functions for the workflow and the page navigation */
/* Should be a page visible or/and editable                */
/* ******************************************************* */

/**
 * Should the page for standing data be accessible?
 *
 * This page should be accessible, if the project has at least one proposal and the project profilassessment is complete
 */
export const pageProjectStandingDataIsAccessible = (project: IProject, challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(project)
  && !isEmptyNullOrUndefinedObject(challenge)
  && checkForGeneralAccessProposalPageRequirements(project)
  && challengeTypeAllowedForSelection(challenge["@type"])

/**
 * Should the page for proposal concretization be visible?
 *
 * The page should be visible, if the challenge has concretizations defined.
 */
export const pageProposalConcretizationIsVisible = (challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(challenge)
  && challengeTypeAllowedForSelection(challenge["@type"])
  && hasConcretizations(challenge)

/**
 * Should be the page of a proposal for concretization accessible?
 *
 * The page should be accessible, if the challenge has concretizations defined and the project profilassessment is complete
 */
export const pageProposalConcretizationIsAccessible = (challenge: IChallenge, project: IProject): boolean =>
  !isEmptyNullOrUndefinedObject(project)
  && pageProposalConcretizationIsVisible(challenge)
  && standingDataIsComplete(project)
  && checkForGeneralAccessProposalPageRequirements(project)


/**
 * Should the page for managing the attachments for a proposal be visible?
 *
 * The page should be visible, if the challenge requires at least one attachment.
 */
export const pageProposalAttachmentIsVisible = (challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(challenge)
  && challengeTypeAllowedForSelection(challenge["@type"])
  && atLeastOneAttachmentIsRequired(challenge)

/**
 * Should the page to manage the attachments for a proposal be accessible?
 *
 * The page should be accessible, if the challenge requires at least one attachment and the project profilassessment is complete
 */
export const pageProposalAttachmentIsAccessible = (challenge: IChallenge, project: IProject): boolean => challenge
  && !isEmptyNullOrUndefinedObject(project)
  && pageProposalAttachmentIsVisible(challenge)
  && standingDataIsComplete(project)
  && checkForGeneralAccessProposalPageRequirements(project)

/**
 * Should the page for submitting a proposal be visible?
 *
 * The page should be visible, if the state is not submitted or finished and if the proposal is active
 */
export const pageProposalSubmissionIsVisible = (proposal: IProposal | IFundApplication, challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(proposal)
  && !isEmptyNullOrUndefinedObject(challenge)
  && proposal?.state === ProposalState.Preparing
  && proposal?.active
  && challengeTypeAllowedForSelection(challenge["@type"])

/**
 * Should be the page proposal submission accessible?
 *
 * The page should be accessible, if the state is not submitted or finished
 * and if the proposal is active and the project profilassessment is complete.
 */
export const pageProposalSubmissionIsAccessible = (proposal: IProposal | IFundApplication, challenge: IChallenge, project: IProject): boolean =>
  pageProposalSubmissionIsVisible(proposal, challenge)
  && standingDataIsComplete(project)
  && requiredAttachmentsAreAttached(challenge, proposal)
  && proposal.concretizationSelfAssessment === SelfAssessment.Complete
  && checkForGeneralAccessProposalPageRequirements(project)

/**
 * Should be the page for inactive proposals visible?
 *
 * The page should be visible, if the state is not submitted or finished and if the proposal is not active
 */
export const pageProposalInActiveIsVisible = (proposal: IProposal | IFundApplication, challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(proposal)
  && !isEmptyNullOrUndefinedObject(challenge)
  && !proposal?.active
  && challengeTypeAllowedForSelection(challenge["@type"])

/**
 * Should be the page for inactive proposals accessible?
 *
 * The page should be accessible, if the state is not submitted or finished
 * and if the proposal is not active and the project profilassessment is complete.
 */
export const pageProposalInActiveIsAccessible = (proposal: IProposal | IFundApplication, challenge: IChallenge, project: IProject): boolean =>
  pageProposalInActiveIsVisible(proposal, challenge)
  && !isEmptyNullOrUndefinedObject(project)
  && standingDataIsComplete(project)
  && requiredAttachmentsAreAttached(challenge, proposal)
  && proposal.concretizationSelfAssessment === SelfAssessment.Complete
  && checkForGeneralAccessProposalPageRequirements(project)

/* Should be the page proposal post submission visible?
*
* The page should be visible, if the state is submitted or finished.
*/
export const pageProposalPostSubmissionIsVisible = (proposal: IProposal | IFundApplication, challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(proposal)
  && !isEmptyNullOrUndefinedObject(challenge)
  && (proposal?.state !== ProposalState.Preparing)
  && proposal?.active
  && challengeTypeAllowedForSelection(challenge["@type"])

/* Should be the page proposal post submission accessible?
*
* The page should be accessible, if the state is submitted or finished
* and the project profilassessment is complete.
*/
export const pageProposalPostSubmissionIsAccessible = (proposal: IProposal | IFundApplication, challenge: IChallenge, project: IProject): boolean =>
  pageProposalPostSubmissionIsVisible(proposal, challenge)
  && !isEmptyNullOrUndefinedObject(project)
  && standingDataIsComplete(project)
  && requiredAttachmentsAreAttached(challenge, proposal)
  && proposal.concretizationSelfAssessment === SelfAssessment.Complete
  && checkForGeneralAccessProposalPageRequirements(project)

/**
 * Should be the page for proposal details, where the corresponding challenge module is inactive be visible?
 *
 * The page should be visible, if for the corresponding challenge of the proposal the challenge module is not active
 */
export const pageProposalChallengeModuleInActiveIsVisible = (challenge: IChallenge): boolean =>
  !isEmptyNullOrUndefinedObject(challenge)
  && !challengeTypeAllowedForSelection(challenge["@type"])

/**
 * Should be the page for proposal details, where the corresponding challenge module is inactive accessible?
 *
 * The page should be accessible, if for the corresponding challenge of the proposal the challenge module is not active
 * and if the project is active and not locked
 */
export const pageProposalChallengeModuleInActiveIsAccessible = (challenge: IChallenge, project: IProject): boolean =>
  !isEmptyNullOrUndefinedObject(challenge)
  && !isEmptyNullOrUndefinedObject(project)
  && !challengeTypeAllowedForSelection(challenge["@type"])
  && checkForGeneralAccessProposalPageRequirements(project)
// #endregion
